import React from 'react';
import { cn } from '../utils/utils';

interface StepperProps {
    steps: string[];
    currentStep: number;
    className?: string;
}

/**
 * Shared multi-step progress bar for wizard flows (series-booking, station
 * creation). Design-system tokens only (tenant `primary` + grayscale via
 * `foreground`), no raw gray/green hex. Done and current both read as the tenant
 * primary; done is filled with a check, current is filled with its number and
 * ringed, upcoming is a muted foreground tint.
 *
 * ── Why the widths are spelled out (QA #2439, defect E) ─────────────────────
 *
 * The row is `flex` and does not wrap, so the four station-wizard steps ran off
 * the right edge of their own card at ~800px and "Produkt" was cut in half. The
 * connectors took `flex-1` while the step items had no shrink control at all,
 * which is the wrong way round: a connector is decoration and a label is
 * content.
 *
 * Three rules keep it inside the card at every width:
 *  - the numbered circle never shrinks (`shrink-0`) — it is the one part that
 *    must stay legible;
 *  - the label may shrink and then truncate, rather than push the row wider;
 *  - below `sm` only the CURRENT step keeps its label. Four German words plus
 *    four circles do not fit a 390px phone, and truncating all four to three
 *    characters tells the operator less than showing the one they are on.
 */
const Stepper: React.FC<StepperProps> = ({ steps, currentStep, className }) => (
    <div className={cn('flex items-center rounded-lg bg-text-contrast p-4 shadow-sm', className)}>
        {steps.map((label, index) => {
            const isDone = index < currentStep;
            const isCurrent = index === currentStep;
            return (
                <React.Fragment key={label}>
                    <div className={'flex min-w-0 items-center gap-2'}>
                        <div
                            className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                                isDone && 'bg-primary text-text-buttonContrast',
                                isCurrent && 'bg-primary text-text-buttonContrast ring-2 ring-primary/30',
                                !isDone && !isCurrent && 'bg-foreground/10 text-foreground/50',
                            )}
                        >
                            {isDone ? '✓' : index + 1}
                        </div>
                        <span
                            className={cn(
                                'truncate text-sm',
                                isCurrent
                                    ? 'font-semibold text-foreground'
                                    : 'hidden text-foreground/50 sm:inline',
                            )}
                        >
                            {label}
                        </span>
                    </div>
                    {index < steps.length - 1 && (
                        <div
                            className={cn(
                                'mx-2 h-0.5 min-w-[0.75rem] flex-1',
                                isDone ? 'bg-primary' : 'bg-foreground/10',
                            )}
                        />
                    )}
                </React.Fragment>
            );
        })}
    </div>
);

export default Stepper;
